import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Form,
  getValueFromEvent,
  Input,
  Modal,
  notification,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { useEffect, useState } from 'react';
import {
  createInvoiceProfile,
  deleteInvoiceProfile,
  getInvoiceProfiles,
  IInvoiceProfileInput,
  updateInvoiceProfile,
} from 'services/invoice-profile';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import { IInvoiceProfile } from 'utilities/invoice-profile';

const textFields: { name: keyof IInvoiceProfileInput; label: string }[] = [
  { name: 'companyName', label: 'Company Name' },
  { name: 'companyEmail', label: 'Email' },
  { name: 'companyNumber', label: 'Phone' },
  { name: 'companyWebsite', label: 'Website' },
  { name: 'companyBinNo', label: 'BIN' },
  { name: 'companyVatNo', label: 'VAT Number' },
  { name: 'address', label: 'Address' },
];

// Aziz Auto A1-A3: Settings > Invoices. Shown only while `sale.invoiceProfiles` is on.
export const InvoiceProfilesSettingCard = () => {
  const [profiles, setProfiles] = useState<IInvoiceProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingProfile, setEditingProfile] = useState<IInvoiceProfile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const loadProfiles = async () => {
    setIsLoading(true);
    try {
      setProfiles(await getInvoiceProfiles());
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err?.message });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const openModal = (profile: IInvoiceProfile | null) => {
    setEditingProfile(profile);
    form.resetFields();
    form.setFieldsValue(profile ?? { isDefault: profiles.length === 0 });
    setIsModalOpen(true);
  };

  const saveProfile = async () => {
    const values = await form.validateFields();
    setIsSaving(true);
    try {
      const input: IInvoiceProfileInput = {
        label: values.label,
        isDefault: !!values.isDefault,
        removeLogo: !!values.removeLogo,
      };
      for (const { name } of textFields) {
        (input as any)[name] = values[name] ?? '';
      }
      const logoFile = values.logo?.[0];
      if (
        logoFile?.originFileObj &&
        isJpgOrPngFile(logoFile.type || '', logoFile.name || '') &&
        isLessThanSize(logoFile.size || 0, MAX_UPLOAD_FILE_SIZE_MB)
      ) {
        input.logo = { base64String: await file2Base64(logoFile) };
      }
      if (editingProfile) {
        await updateInvoiceProfile(editingProfile.id, input);
      } else {
        await createInvoiceProfile(input);
      }
      setIsModalOpen(false);
      await loadProfiles();
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err?.message });
    }
    setIsSaving(false);
  };

  const makeDefault = async (profile: IInvoiceProfile) => {
    try {
      await updateInvoiceProfile(profile.id, {
        label: profile.label,
        companyName: profile.companyName ?? '',
        companyEmail: profile.companyEmail ?? '',
        companyNumber: profile.companyNumber ?? '',
        companyWebsite: profile.companyWebsite ?? '',
        companyBinNo: profile.companyBinNo ?? '',
        companyVatNo: profile.companyVatNo ?? '',
        address: profile.address ?? '',
        isDefault: true,
      });
      await loadProfiles();
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err?.message });
    }
  };

  const removeProfile = async (profile: IInvoiceProfile) => {
    try {
      await deleteInvoiceProfile(profile.id);
      await loadProfiles();
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err?.message });
    }
  };

  return (
    <Card
      title="Invoice headers"
      size="small"
      style={{ marginTop: 16 }}
      headStyle={{ fontSize: '16px', padding: '16px 24px', fontWeight: 'bold' }}
      extra={
        <Button type="primary" onClick={() => openModal(null)}>
          Add header
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">
        Choose one of these on a sale. A blank field prints the value from Company Information. With
        no headers, every invoice uses Company Information. A sale keeps the header it was made
        with, even after the default changes or the header is deleted.
      </Typography.Paragraph>
      <Table rowKey="id" dataSource={profiles} loading={isLoading} pagination={false}>
        <Table.Column<IInvoiceProfile>
          title="Header"
          dataIndex="label"
          render={(label, profile) => (
            <Space>
              {profile.logoUrl && <Avatar size="small" src={profile.logoUrl} />}
              {label}
              {profile.isDefault && <Tag color="blue">Default</Tag>}
            </Space>
          )}
        />
        <Table.Column<IInvoiceProfile> title="Company Name" dataIndex="companyName" />
        <Table.Column<IInvoiceProfile> title="Phone" dataIndex="companyNumber" />
        <Table.Column<IInvoiceProfile>
          title="Actions"
          render={(_, profile) => (
            <Space>
              <Button size="small" onClick={() => openModal(profile)}>
                Edit
              </Button>
              {!profile.isDefault && (
                <Button size="small" onClick={() => makeDefault(profile)}>
                  Make default
                </Button>
              )}
              <Popconfirm
                title="Delete this header? Sales made with it keep printing it."
                onConfirm={() => removeProfile(profile)}
              >
                <Button size="small" danger>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={editingProfile ? 'Edit invoice header' : 'Add invoice header'}
        visible={isModalOpen}
        onOk={saveProfile}
        okText="Save"
        confirmLoading={isSaving}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Header name (shown on the sale)"
            name="label"
            rules={[{ required: true, message: 'Give the header a name' }]}
          >
            <Input />
          </Form.Item>
          {textFields.map(({ name, label }) => (
            <Form.Item
              key={name}
              label={label}
              name={name}
              rules={
                name === 'companyEmail' ? [{ type: 'email', message: 'Enter an email' }] : undefined
              }
            >
              <Input placeholder="Blank uses Company Information" />
            </Form.Item>
          ))}
          <Form.Item
            label="Logo"
            name="logo"
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
          >
            <Upload
              listType="picture"
              accept="image/png, image/jpeg"
              maxCount={1}
              beforeUpload={beforeImageUploadValidation}
            >
              <Button>Choose logo</Button>
            </Upload>
          </Form.Item>
          {editingProfile?.logoUrl && (
            <Form.Item name="removeLogo" valuePropName="checked">
              <Checkbox>Remove this header's logo (use the company logo)</Checkbox>
            </Form.Item>
          )}
          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox disabled={editingProfile?.isDefault}>Default for new sales</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
